/**
 * Offline-attendance readiness (implementation plan FE-5, D9): one PURE
 * function from everything the app knows to ONE state with ONE reason —
 * `ready`, `degraded` (capture works; a warning applies), `blocked` (capture
 * is refused for a reason the employee or an admin can act on), `preparing`
 * (not provisioned yet) or `online_only` (offline capture does not apply).
 *
 * The rules are checked in the plan's order and the FIRST match wins:
 *   1 build flag off ⇒ online_only/build_disabled
 *   2 native shell without native capture enabled ⇒ online_only/native_disabled
 *   3 environment cannot store punches securely ⇒ online_only/unsupported_environment
 *   4 no session / scope ⇒ blocked/signed_out
 *   5 backend unsupported | disabled | forbidden ⇒ online_only/…
 *   6 policy requires a photo or location ⇒ online_only/evidence_required (D15)
 *     (7–8 exception: failing storage on a device that never held offline data
 *      ⇒ online_only/storage_unavailable — see the fail-safe additions)
 *   7 data key lost / unavailable ⇒ blocked/key_unavailable
 *   8 storage failed ⇒ blocked/storage_unavailable
 *   9 no usable context, grant or policy ⇒ preparing/(provisioning | not_provisioned
 *     | scope_mismatch when the stored context failed its binding)
 *  10 grant expired ⇒ blocked/grant_expired
 *  11 clock more than 5 min before the grant start ⇒ blocked/clock_unsafe
 *  12 clock verdict unsafe ⇒ blocked/clock_unsafe
 *  13 no confirmed snapshot ⇒ preparing/not_provisioned
 *  14 more than one open row ⇒ blocked/ambiguous_attendance
 *  15 warnings, in order — refresh_due, storage_not_persistent, sync_failing —
 *     ⇒ degraded (reason = the first) else ready.
 * Then the QUEUE OVERRIDE: an `online_only` or `preparing` result while
 * unsettled punches exist becomes blocked/queue_waiting — the app must never
 * fall back to an untracked legacy POST while queued history exists (D9;
 * spec §7). A `blocked` reason is already more specific and is kept; `ready`
 * and `degraded` keep capturing onto the queue.
 *
 * Fail-safe additions where the plan is silent (each documented at its rule):
 *  - rule 5: while the backend has not answered this session (`unknown`), a
 *    STORED context with the capability off says what ERPat last said —
 *    `disabled` ⇒ disabled_by_company, `not_ready` ⇒ unsupported_backend —
 *    instead of reading as "not provisioned yet";
 *  - rules 7–8 exception, checked before rule 7 (where rule 8 and D9 / Review
 *    Focus 5 collide): storage that FAILED on a device that held no offline
 *    data at all when the session resolved its storage (`noPriorData` — the
 *    database did not exist), with a queue known to be EMPTY and nothing — no
 *    answer this session, no context — saying capture is on, reads
 *    online_only/storage_unavailable. Nothing was ever provisioned or queued
 *    there, so ERPat's own POST stays the path it had before offline punches:
 *    D9 forbids that POST only while queued history may exist, Review Focus 5
 *    only once provisioned. ERPat saying capture is ON (this session, or a
 *    context the session saved before the store failed), a count that is not 0,
 *    or a device that may have held data keeps the plan's rules 7–8. Checked
 *    before rule 7 because on the web the data key lives in that same database,
 *    so a failing store fails the key too;
 *  - rule 9: a context without a policy, or a grant whose validity window
 *    cannot be read, is not usable (capture needs both);
 *  - rule 9: a stored context that FAILED ITS BINDING (`contextIntegrity:
 *    'scope_mismatch'`, FE-7 `loadLocal`) is not used at all — not for rule 5's
 *    stored capability, not for rule 6's policy — and reads
 *    preparing/scope_mismatch: like `not_provisioned`, it is fixed by
 *    provisioning again online, so an empty queue keeps the online path (D9)
 *    and a queue becomes blocked/queue_waiting. The reason is the plan's
 *    `scope_mismatch` (copy: amendment C7), which nothing else produces. The
 *    input is REQUIRED and fails closed: any value but `ok` is a failed binding;
 *  - rule 12: a corrected "now" that is not a number is an unsafe clock — a
 *    NaN would otherwise slip through every comparison below as "fine";
 *  - rule 13: a stored snapshot the projection cannot build on is no snapshot.
 *    The check is the projection's OWN validator (`isBaseSnapshot()`), so
 *    readiness never reports `ready` for a record the projection refuses as
 *    `no_base` (an open row without its id or revision, a tap count that
 *    disagrees with on/off break, an unreadable `as_of` or tap…) — which would
 *    show "ready" with nothing to offer and no reason — and never throws a
 *    TypeError out of this pure function;
 *  - queue override: only a queue known to be EMPTY (`unsettled === 0`) keeps
 *    the online path; a count that is not a number may hide punches.
 *
 * `clock` and `nowMs` come from the clock monitor (amendments C10, C12): at
 * capture, `ClockMonitor.stamp()`'s `verdict` and `correctedNowMs` — the
 * CORRECTED (server-aligned) time the capture would be stamped with, from the
 * calibration the monitor holds, never the stored context's own; `NaN` when
 * there is none (⇒ blocked/clock_unsafe). Pass `syncFailingSinceMs` on the
 * same time base.
 */
import {
  OFFLINE_GRANT_EARLY_TOLERANCE_MS,
  OFFLINE_SYNC_FAILING_WARN_MS,
} from '@/contracts/offline-attendance-policy';
import { isBaseSnapshot } from '@/services/offline/attendance-projection';
import type { ClockVerdict } from '@/services/offline/clock-health';
import type {
  ConfirmedSnapshotRecord,
  OfflineContextRecord,
  OfflineReadiness,
  OfflineReadinessReason,
  OfflineReadinessState,
  StoredGrant,
} from '@/types/offline-attendance';

export interface ReadinessInputs {
  /** `OFFLINE_ATTENDANCE_ENABLED` (utils/env). */
  buildEnabled: boolean;
  /** Running inside the Capacitor shell. */
  nativePlatform: boolean;
  /** `OFFLINE_ATTENDANCE_NATIVE_ENABLED` (utils/env, D6). */
  nativeEnabled: boolean;
  /** IndexedDB + WebCrypto (+ a monotonic clock) are available. */
  environmentSupported: boolean;
  storage: 'unknown' | 'ready' | 'failed';
  /**
   * The device held NO offline data when the session resolved its storage: the
   * offline database did not exist, so nothing was ever provisioned or queued
   * here. Only the 7–8 exception reads it; absent ⇒ `false` (the plan's rules).
   */
  noPriorData?: boolean;
  /** `navigator.storage.persisted()` — null when unknown. */
  storagePersisted: boolean | null;
  key: 'unknown' | 'ready' | 'lost' | 'unavailable';
  authenticated: boolean;
  scopeResolved: boolean;
  /** What ERPat said this session; `unknown` until it answers (offline boot). */
  backend: 'unknown' | 'unsupported' | 'disabled' | 'forbidden' | 'enabled';
  /** A provisioning request is in flight. */
  provisioning: boolean;
  context: OfflineContextRecord | null;
  /**
   * FE-7 `loadLocal().integrity` for `context` — REQUIRED, so the orchestrator
   * cannot forget it. Anything but `ok` ⇒ the stored context failed its binding
   * (or its integrity is unknown) and is not trusted at all (rule 9). Pass `ok`
   * only when no local load has produced a context to distrust (`context: null`)
   * or when that load reported `ok`.
   */
  contextIntegrity: 'ok' | 'scope_mismatch';
  confirmed: ConfirmedSnapshotRecord | null;
  /** The clock monitor's verdict — at capture, `ClockMonitor.stamp().verdict` (C10). */
  clock: ClockVerdict;
  /** Unsettled events of this scope (pending + in flight); anything but 0 counts as "may hold punches". */
  unsettled: number;
  /** When uploads started failing (same time base as `nowMs`), else null. */
  syncFailingSinceMs: number | null;
  /**
   * Corrected (server-aligned) now from the calibration the clock monitor
   * holds — at capture, `ClockMonitor.stamp().correctedNowMs` — never from the
   * stored context's calibration; `NaN` when the monitor holds none (C12).
   */
  nowMs: number;
}

function result(state: Exclude<OfflineReadinessState, 'ready' | 'degraded'>, reason: OfflineReadinessReason): OfflineReadiness {
  return { state, reason, warnings: [] };
}

/** Rule 5: the backend's answer this session, else what the (trusted) stored context last recorded. */
function backendStatus(
  backend: ReadinessInputs['backend'],
  context: OfflineContextRecord | null,
): ReadinessInputs['backend'] {
  if (backend !== 'unknown') return backend;
  const capability = context?.capability;
  if (capability !== undefined && !capability.offlineAttendance) {
    return capability.reason === 'not_ready' ? 'unsupported' : 'disabled';
  }
  return 'unknown';
}

/** Rule 9: a grant whose window can be compared at all. */
function usableGrant(grant: StoredGrant): boolean {
  return Number.isFinite(grant.validFromMs) && Number.isFinite(grant.validUntilMs) && Number.isFinite(grant.issuedAtMs);
}

/** Rules 1–15, without the queue override. */
function evaluate(i: ReadinessInputs): OfflineReadiness {
  // 1–3: offline capture does not apply to this build / platform / browser.
  if (!i.buildEnabled) return result('online_only', 'build_disabled');
  if (i.nativePlatform && !i.nativeEnabled) return result('online_only', 'native_disabled');
  if (!i.environmentSupported) return result('online_only', 'unsupported_environment');

  // 4: nothing can be captured — or sent — without a session.
  if (!i.authenticated || !i.scopeResolved) return result('blocked', 'signed_out');

  // A stored context that failed its binding says nothing trustworthy (rule 9).
  // Fail closed: an integrity that is not exactly `ok` — a caller that went
  // through `unknown` — is treated as a failed binding, never as a pass.
  const mismatched = i.contextIntegrity !== 'ok';
  const context = mismatched ? null : i.context;

  // 5: ERPat's answer about the capability.
  const backend = backendStatus(i.backend, context);
  if (backend === 'unsupported') return result('online_only', 'unsupported_backend');
  if (backend === 'disabled') return result('online_only', 'disabled_by_company');
  if (backend === 'forbidden') return result('online_only', 'permission_missing');

  // 6: evidence cannot be captured offline in Release 1 (D15).
  const policy = context?.policy ?? null;
  if (policy !== null && (policy.requires_photo || policy.requires_location)) {
    return result('online_only', 'evidence_required');
  }

  // 7–8 exception (module docblock): a store that fails on a device that never
  // held offline data, with nothing queued and nothing saying capture is on.
  // After rule 5, `unknown` means ERPat has not answered this session and no
  // trusted context says capture is off; `i.context` rules out the rest — a
  // context with capture on, or one failing its binding (still a context).
  if (
    i.noPriorData === true &&
    i.storage === 'failed' &&
    backend === 'unknown' &&
    i.context === null &&
    i.unsettled === 0
  ) {
    return result('online_only', 'storage_unavailable');
  }

  // 7–8: the device cannot keep a punch safely.
  if (i.key === 'lost' || i.key === 'unavailable') return result('blocked', 'key_unavailable');
  if (i.storage === 'failed') return result('blocked', 'storage_unavailable');

  // 9: nothing provisioned that a capture could be authorized and shaped by.
  const grant = context?.grant ?? null;
  if (context === null || grant === null || policy === null || !usableGrant(grant)) {
    if (i.provisioning) return result('preparing', 'provisioning');
    return result('preparing', mismatched ? 'scope_mismatch' : 'not_provisioned');
  }

  // 10–12: the grant's window and the clock that is read against it.
  if (!Number.isFinite(i.nowMs)) return result('blocked', 'clock_unsafe');
  if (i.nowMs > grant.validUntilMs) return result('blocked', 'grant_expired');
  if (i.nowMs < grant.validFromMs - OFFLINE_GRANT_EARLY_TOLERANCE_MS) return result('blocked', 'clock_unsafe');
  if (i.clock.status === 'unsafe') return result('blocked', 'clock_unsafe');

  // 13–14: a confirmed base the projection can build on — judged by the
  // projection's own validator (the record comes back from storage typed but
  // not proven). A readable snapshot with several open rows is rule 14's.
  if (i.confirmed === null || !isBaseSnapshot(i.confirmed)) return result('preparing', 'not_provisioned');
  if (i.confirmed.snapshot.open_rows > 1) return result('blocked', 'ambiguous_attendance');

  // 15: capture works; say what deserves attention.
  const warnings: OfflineReadinessReason[] = [];
  if (i.nowMs > grant.issuedAtMs + policy.refresh_warn_s * 1_000) warnings.push('refresh_due');
  if (i.storagePersisted === false) warnings.push('storage_not_persistent');
  if (i.syncFailingSinceMs !== null && i.nowMs - i.syncFailingSinceMs > OFFLINE_SYNC_FAILING_WARN_MS) {
    warnings.push('sync_failing');
  }
  const first = warnings[0];
  return first === undefined
    ? { state: 'ready', reason: null, warnings: [] }
    : { state: 'degraded', reason: first, warnings };
}

export function computeReadiness(i: ReadinessInputs): OfflineReadiness {
  const readiness = evaluate(i);
  // Only a queue KNOWN to be empty keeps the online path: a count that is not a
  // number (not read yet, or broken) may hide punches, and must never open the
  // legacy POST (D9).
  const queueMayHoldPunches = i.unsettled !== 0;
  if ((readiness.state === 'online_only' || readiness.state === 'preparing') && queueMayHoldPunches) {
    return { state: 'blocked', reason: 'queue_waiting', warnings: [] };
  }
  return readiness;
}
